import { InjectionKey, Ref, provide, inject, ref, readonly, App } from "vue";

export interface AntlerModule {
    id: string;
    name: string;
    version?: string;
    init?: () => void | Promise<void>;
    destroy?: () => void | Promise<void>;
    [key: string]: unknown;
}

export interface AntlerProvider {
    modules: Map<string, AntlerModule>;
    registerModule: (module: AntlerModule) => void;
    deregisterModule: (moduleId: string) => void;
    getModule: (moduleId: string) => AntlerModule | undefined;
    hasModule: (moduleId: string) => boolean;
    listModules: () => AntlerModule[];
    devMode: Ref<boolean>;
    toggleDevMode: () => void;
}

export const AntlerProviderKey: InjectionKey<AntlerProvider> =
    Symbol("AntlerProvider");

export function createAntlerProvider(): AntlerProvider {
    const modules = new Map<string, AntlerModule>();
    const devMode = ref(false);

    const registerModule = (module: AntlerModule) => {
        if (modules.has(module.id)) {
            console.warn(
                `[Antler] Module "${module.id}" is already registered`,
            );
            return;
        }

        // Execute init synchronously if possible, or schedule it
        if (module.init) {
            try {
                const result = module.init();
                if (result instanceof Promise) {
                    result.catch(error => {
                        console.error(
                            `[Antler] Failed to initialize module "${module.id}":`,
                            error,
                        );
                    });
                }
            } catch (error) {
                console.error(
                    `[Antler] Failed to initialize module "${module.id}":`,
                    error,
                );
            }
        }

        modules.set(module.id, module);

        if (devMode.value) {
            console.log(`[Antler] Module "${module.id}" registered`);
        }
    };

    const deregisterModule = (moduleId: string) => {
        const module = modules.get(moduleId);
        if (!module) {
            console.warn(`[Antler] Module "${moduleId}" not found`);
            return;
        }

        if (module.destroy) {
            try {
                const result = module.destroy();
                if (result instanceof Promise) {
                    result.catch(error => {
                        console.error(
                            `[Antler] Failed to destroy module "${moduleId}":`,
                            error,
                        );
                    });
                }
            } catch (error) {
                console.error(
                    `[Antler] Failed to destroy module "${moduleId}":`,
                    error,
                );
            }
        }

        modules.delete(moduleId);

        if (devMode.value) {
            console.log(`[Antler] Module "${moduleId}" deregistered`);
        }
    };

    const getModule = (moduleId: string): AntlerModule | undefined => {
        return modules.get(moduleId);
    };

    const hasModule = (moduleId: string): boolean => {
        return modules.has(moduleId);
    };

    const listModules = (): AntlerModule[] => {
        return Array.from(modules.values());
    };

    const toggleDevMode = () => {
        devMode.value = !devMode.value;
    };

    return {
        modules: readonly(modules) as Map<string, AntlerModule>,
        registerModule,
        deregisterModule,
        getModule,
        hasModule,
        listModules,
        devMode: readonly(devMode),
        toggleDevMode,
    };
}

// Global provider instance - initialized immediately
const globalAntlerProvider = createAntlerProvider();

// Plugin for Vue app installation
export const AntlerPlugin = {
    install(app: App, options?: { modules?: AntlerModule[] }) {
        // Provide the global provider to the app
        app.provide(AntlerProviderKey, globalAntlerProvider);

        // Register default modules if provided
        if (options?.modules) {
            options.modules.forEach(module => {
                globalAntlerProvider.registerModule(module);
            });
        }
    },
};

export function provideAntler(): AntlerProvider {
    provide(AntlerProviderKey, globalAntlerProvider);
    return globalAntlerProvider;
}

export function useAntler(): AntlerProvider {
    // Try inject first for proper context isolation
    const injected = inject(AntlerProviderKey, null);
    // Fall back to global instance
    return injected || globalAntlerProvider;
}

export function useAntlerOrThrow(): AntlerProvider {
    const provider = useAntler();
    if (!provider) {
        throw new Error(
            "[Antler] Provider not found. Make sure to call provideAntler() in a parent component or install the AntlerPlugin.",
        );
    }
    return provider;
}

// Direct access to global provider for setup outside of components
export function getGlobalAntlerProvider(): AntlerProvider {
    return globalAntlerProvider;
}
